import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import {
  fetchCourses,
  createCourse,
  deleteCourse,
  clearCurrentCourse,
} from "../../redux/slices/courseSlice";
import { pickLocalized } from "../../lib/localized";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { useToast } from "../../components/ui/use-toast";
import ImageUploadField from "../../components/files/ImageUploadField";
import { MultilingualInput } from "../../components/common/MultilingualInput";
import { hasEnglishText, pruneMultilingual } from "../../lib/multilingual-helpers";
import { resolveAssetUrl } from "../../lib/asset-url";

function Courses() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { courses, loading } = useSelector((state) => state.course);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState({
    title: { en: "" },
    description: { en: "" },
    duration_days: "7",
    taught_by: "",
    thumbnail_url: "",
    course_start: "",
    is_active: true,
  });

  useEffect(() => {
    dispatch(clearCurrentCourse());
    dispatch(fetchCourses({}));
  }, [dispatch]);

  const openCreate = () => {
    setForm({
      title: { en: "" },
      description: { en: "" },
      duration_days: "7",
      taught_by: "",
      thumbnail_url: "",
      course_start: "",
      is_active: true,
    });
    setDialogOpen(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!hasEnglishText(form.title)) {
      toast({
        title: "Validation",
        description: "English title is required",
        variant: "destructive",
      });
      return;
    }
    const duration = parseInt(form.duration_days, 10);
    if (Number.isNaN(duration) || duration < 1) {
      toast({
        title: "Validation",
        description: "Duration must be at least 1 day",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      await dispatch(
        createCourse({
          title: pruneMultilingual(form.title),
          description: pruneMultilingual(form.description),
          duration_days: duration,
          taught_by: form.taught_by.trim() || null,
          thumbnail_url: form.thumbnail_url.trim() || null,
          course_start: form.course_start || null,
          is_active: form.is_active,
        })
      ).unwrap();
      toast({ title: "Created", description: "Course saved" });
      setDialogOpen(false);
      dispatch(fetchCourses({}));
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Could not create course",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await dispatch(deleteCourse(toDelete.id)).unwrap();
      toast({ title: "Deleted", description: "Course removed" });
      setDeleteOpen(false);
      setToDelete(null);
      dispatch(fetchCourses({}));
    } catch (err) {
      toast({
        title: "Error",
        description: typeof err === "string" ? err : "Could not delete",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Courses</h1>
          <p className="text-muted-foreground">
            Manage structured learning courses and syllabi days
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          New course
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All courses</CardTitle>
          <CardDescription>
            Each course can include titles and descriptions in every active
            language.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && courses.length === 0 ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[72px]">Thumb</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {courses.map((course) => (
                  <TableRow key={course.id}>
                    <TableCell>
                      {course.thumbnail_url ? (
                        <img
                          src={resolveAssetUrl(course.thumbnail_url)}
                          alt=""
                          className="h-10 w-14 rounded object-cover border bg-muted"
                        />
                      ) : (
                        <div className="h-10 w-14 rounded border bg-muted/50" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium max-w-[280px] truncate">
                      <Link
                        to={`/courses/${course.id}`}
                        className="text-primary hover:underline"
                      >
                        {pickLocalized(course.title) || "(untitled)"}
                      </Link>
                    </TableCell>
                    <TableCell>{course.days?.length ?? 0}</TableCell>
                    <TableCell>{course.duration_days} d</TableCell>
                    <TableCell>
                      <Badge
                        variant={course.is_active ? "default" : "secondary"}
                      >
                        {course.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/courses/${course.id}`}>
                          <Pencil className="w-4 h-4 mr-1" />
                          Manage
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => {
                          setToDelete(course);
                          setDeleteOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!loading && courses.length === 0 && (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No courses yet. Create one to get started.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={submitCreate}>
            <DialogHeader>
              <DialogTitle>New course</DialogTitle>
              <DialogDescription>
                Enter the course title and description in each language you
                support.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <MultilingualInput
                label="Title"
                value={form.title}
                onChange={(title) => setForm((f) => ({ ...f, title }))}
                placeholder="e.g., 7-day Ramadan essentials"
                required
              />
              <MultilingualInput
                label="Description"
                value={form.description}
                onChange={(description) =>
                  setForm((f) => ({ ...f, description }))
                }
                placeholder="Short subtitle shown in the app"
                multiline
                rows={2}
              />
              <div className="grid gap-2">
                <Label htmlFor="dur">Duration (days)</Label>
                <Input
                  id="dur"
                  type="number"
                  min={1}
                  value={form.duration_days}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, duration_days: e.target.value }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="taughtBy">Taught by</Label>
                <Input
                  id="taughtBy"
                  value={form.taught_by}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, taught_by: e.target.value }))
                  }
                  placeholder="Instructor name"
                />
              </div>
              <ImageUploadField
                label="Course thumbnail"
                hint="Optional image for the app courses list."
                value={form.thumbnail_url}
                onChange={(url) =>
                  setForm((f) => ({ ...f, thumbnail_url: url }))
                }
              />
              <div className="grid gap-2">
                <Label htmlFor="courseStart">Course start date</Label>
                <Input
                  id="courseStart"
                  type="date"
                  value={form.course_start}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, course_start: e.target.value }))
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="active"
                  checked={form.is_active}
                  onCheckedChange={(checked) =>
                    setForm((f) => ({ ...f, is_active: checked }))
                  }
                />
                <Label htmlFor="active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete course?</DialogTitle>
            <DialogDescription>
              {toDelete
                ? `"${pickLocalized(toDelete.title)}" will be permanently removed unless enrollments exist.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Courses;
